import { useMemo, useCallback } from 'react';
import { useFilters } from '@/contexts/FilterContext';
import type { Unit, Company } from '@/types';

/**
 * Entity that can be matched hierarchically (must have company_id and primary_group_id)
 */
interface HierarchicalEntity {
  company_id?: string | null;
  primary_group_id?: string | null;
}

/**
 * Hook for hierarchical search that expands search queries to include
 * all units under matching companys/groups.
 *
 * When user searches "NBC Universal" (a company), shows all units under that company.
 * When user searches "Jurassic World" (a group), shows that group + all child groups.
 * Also preserves existing direct string matching for unit codes, addresses, etc.
 */
export function useHierarchicalSearch(searchQuery: string) {
  const { companies, groups, companyGroupsMap, groupDescendantsMap } = useFilters();

  // Pre-compute the expanded set of group IDs that match the search query
  const expandedGroupIds = useMemo(() => {
    if (!searchQuery) return null;

    const query = searchQuery.toLowerCase().trim();
    if (!query) return null;

    const matchedIds = new Set<string>();

    // Match companies → include ALL their groups
    companies.forEach((company: Company) => {
      if (company.name.toLowerCase().includes(query)) {
        const groupIds = companyGroupsMap.get(company.id);
        if (groupIds) {
          groupIds.forEach(id => matchedIds.add(id));
        }
      }
    });

    // Match groups → include the group + all descendants
    groups.forEach(group => {
      if (group.name.toLowerCase().includes(query)) {
        matchedIds.add(group.id);
        const descendants = groupDescendantsMap.get(group.id);
        if (descendants) {
          descendants.forEach(id => matchedIds.add(id));
        }
      }
    });

    return matchedIds.size > 0 ? matchedIds : null;
  }, [searchQuery, companies, groups, companyGroupsMap, groupDescendantsMap]);

  // Memoized set of matching company IDs for direct company matching
  const matchingCompanyIds = useMemo(() => {
    if (!searchQuery) return null;

    const query = searchQuery.toLowerCase().trim();
    if (!query) return null;

    const matchedIds = new Set<string>();
    companies.forEach((company: Company) => {
      if (company.name.toLowerCase().includes(query)) {
        matchedIds.add(company.id);
      }
    });

    return matchedIds.size > 0 ? matchedIds : null;
  }, [searchQuery, companies]);

  /**
   * Check if any entity with company_id/primary_group_id matches the hierarchical search.
   * Use this for generic entity matching (works with Unit or any similar type).
   *
   * Returns true if:
   * - No search query (show all)
   * - Entity's group is in the expanded group set
   * - Entity's company matches when searching company names
   */
  const matchesHierarchy = useCallback((entity: HierarchicalEntity): boolean => {
    if (!searchQuery) return true;

    // Check hierarchical group matching first
    if (expandedGroupIds && entity.primary_group_id) {
      if (expandedGroupIds.has(entity.primary_group_id)) return true;
    }

    // Check direct company matching (for unassigned units under matching company)
    if (matchingCompanyIds && entity.company_id && matchingCompanyIds.has(entity.company_id)) {
      return true;
    }

    return false;
  }, [searchQuery, expandedGroupIds, matchingCompanyIds]);

  /**
   * Check if a unit matches the hierarchical search criteria.
   * Returns true if:
   * - No search query (show all)
   * - Unit's group is in the expanded group set
   * - Unit's company matches when searching company names
   * - Direct string match on unit code, name, address, or starlinkId
   */
  const matchesUnit = useCallback((unit: Unit): boolean => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase().trim();
    if (!query) return true;

    // Check hierarchical matching first
    if (matchesHierarchy(unit)) return true;

    // Fallback to direct string matching (existing behavior)
    return (
      (unit.name?.toLowerCase().includes(query) ?? false) ||
      (unit.unit_code?.toLowerCase().includes(query) ?? false) ||
      (unit.location?.address?.toLowerCase().includes(query) ?? false) ||
      (unit.starlinkId?.toLowerCase().includes(query) ?? false) ||
      // Also check expanded company/group names for display purposes
      (unit.company?.name?.toLowerCase().includes(query) ?? false) ||
      (unit.primary_group?.name?.toLowerCase().includes(query) ?? false)
    );
  }, [searchQuery, matchesHierarchy]);

  return {
    expandedGroupIds,
    matchingCompanyIds,
    matchesHierarchy,
    matchesUnit
  };
}
